import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue,
  Badge, Flex, Select, Input, InputGroup, InputLeftElement,
  Icon, HStack, Button,
} from '@chakra-ui/react';
import { MdSearch, MdHistory, MdChevronLeft, MdChevronRight } from 'react-icons/md';
import Card from 'components/card/Card';
import { getAuditLogs, getAuditSummary } from '../../services/audit.service';
import AuditChangesCell from 'components/admin/AuditChangesCell';
import AuditChangesModal from 'components/admin/AuditChangesModal';

const ACTION_COLORS = {
  CREATE: 'green',
  UPDATE: 'blue',
  DELETE: 'red',
  LOGIN: 'purple',
  APPROVE: 'teal',
  CANCEL: 'orange',
  CALCULATE: 'cyan',
  CALCULATE_PAYROLL: 'cyan',
  MARK: 'purple',
  TERMINATE: 'orange',
};
const PAGE_SIZE = 20;

function normalizeLogsPayload(payload) {
  if (Array.isArray(payload)) {
    return { data: payload, total: payload.length, limit: PAGE_SIZE, offset: 0 };
  }
  if (payload && Array.isArray(payload.data)) {
    return {
      data: payload.data,
      total: Number(payload.total) || payload.data.length,
      limit: Number(payload.limit) || PAGE_SIZE,
      offset: Number(payload.offset) || 0,
    };
  }
  return { data: [], total: 0, limit: PAGE_SIZE, offset: 0 };
}

function normalizeSummaryPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.data)) return payload.data;
  return [];
}

export default function AuditLogView() {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');

  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [moduleFilter, setModuleFilter] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [page, setPage] = useState(0);
  const [selectedLog, setSelectedLog] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const filters = {
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      };
      if (moduleFilter) filters.module = moduleFilter;
      if (actionFilter) filters.action = actionFilter;
      if (search.trim()) filters.search = search.trim();

      const [logsRes, summaryRes] = await Promise.all([
        getAuditLogs(filters),
        getAuditSummary(),
      ]);

      const normalized = normalizeLogsPayload(logsRes?.payload);
      setLogs(normalized.data);
      setTotal(normalized.total);
      setSummary(normalizeSummaryPayload(summaryRes?.payload));
    } catch (e) {
      console.error(e);
      setLogs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, moduleFilter, actionFilter, search]);

  useEffect(() => {
    load();
  }, [load]);

  const commitSearch = (value) => {
    setSearch(value);
    setPage(0);
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    // Al vaciar el input se limpia el filtro sin necesidad de Enter
    if (value === '' && search !== '') commitSearch('');
  };

  const modules = useMemo(
    () => [...new Set(summary.map((s) => s.module))].sort(),
    [summary],
  );
  const actions = useMemo(
    () => [...new Set(summary.map((s) => s.action))].sort(),
    [summary],
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const formatDate = (raw) => {
    if (!raw) return '—';
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return String(raw);
    return d.toLocaleString('es-UY');
  };

  if (loading && logs.length === 0) return null;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card p={4}>
        <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
          <Flex gap={3} flexWrap="wrap">
            <InputGroup w="280px">
              <InputLeftElement>
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Buscar y presionar Enter..."
                value={searchInput}
                onChange={handleSearchChange}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitSearch(searchInput);
                }}
              />
            </InputGroup>
            <Select
              w="180px"
              value={moduleFilter}
              onChange={(e) => {
                setModuleFilter(e.target.value);
                setPage(0);
              }}
            >
              <option value="">Todos los módulos</option>
              {modules.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </Select>
            <Select
              w="140px"
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value);
                setPage(0);
              }}
            >
              <option value="">Todas las acciones</option>
              {actions.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </Select>
          </Flex>
          <Text fontSize="sm" color="gray.500">
            {total} registros
          </Text>
        </Flex>

        {logs.length === 0 ? (
          <Flex justify="center" py={10} direction="column" align="center">
            <Icon as={MdHistory} w={12} h={12} color="gray.300" mb={3} />
            <Text color="gray.500">Sin registros de auditoría</Text>
          </Flex>
        ) : (
          <Box overflowX="auto" opacity={loading ? 0.6 : 1}>
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th borderColor={borderColor}>Fecha</Th>
                  <Th borderColor={borderColor}>Usuario</Th>
                  <Th borderColor={borderColor}>Acción</Th>
                  <Th borderColor={borderColor}>Módulo</Th>
                  <Th borderColor={borderColor}>Cambios</Th>
                </Tr>
              </Thead>
              <Tbody>
                {logs.map((l) => (
                  <Tr key={l.id} _hover={{ bg: rowHover }}>
                    <Td borderColor={borderColor}>
                      <Text fontSize="xs">{formatDate(l.createdDate)}</Text>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Text fontSize="sm" fontWeight="600">
                        {l.userName || (l.userId ? `Usuario #${l.userId}` : 'Sistema')}
                      </Text>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Badge colorScheme={ACTION_COLORS[l.action] || 'gray'}>{l.action}</Badge>
                    </Td>
                    <Td borderColor={borderColor}>
                      <Badge variant="outline">{l.module}</Badge>
                    </Td>
                    <Td borderColor={borderColor} maxW="220px">
                      <AuditChangesCell log={l} onView={setSelectedLog} />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}

        {totalPages > 1 && (
          <Flex justify="center" mt={4}>
            <HStack>
              <Button
                size="sm"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                isDisabled={page === 0 || loading}
                leftIcon={<MdChevronLeft />}
              >
                Ant
              </Button>
              <Text fontSize="sm">
                {page + 1} / {totalPages}
              </Text>
              <Button
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                isDisabled={page >= totalPages - 1 || loading}
                rightIcon={<MdChevronRight />}
              >
                Sig
              </Button>
            </HStack>
          </Flex>
        )}
      </Card>

      <AuditChangesModal
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        log={selectedLog}
      />
    </Box>
  );
}
